/**
 * Agent Connection App Settings
 *
 * Mirrors the service-provider pattern (`serviceProviderAppSettings.ts`):
 * secrets stored in `agentConnections[*].authentication` (the `key` field on
 * `Key` auth, or `parameter` field on `Raw` auth) must not be persisted inline
 * in connections.json. Instead the value is moved to an app setting and the
 * leaf is replaced with an `@appsetting('NAME')` reference. The Standard
 * runtime's `EdgeConnectionsDataProvider.GetResolvedAgentConnections`
 * evaluates these references at execution time.
 */

import {
  escapeAppSettingChars,
  formatAppSettingName,
  formatAppSettingReference,
  isAppSettingReference,
} from './serviceProviderAppSettings';

// Re-exported so callers can resolve names back to settings if needed later.
export { escapeAppSettingChars };

export type AgentAuthentication = {
  type?: string;
  identity?: string;
  key?: string;
  parameter?: string;
  scheme?: string;
  value?: string;
  [k: string]: unknown;
};

export interface AgentAuthenticationAppSettingsResult {
  /** Authentication object with secret leaves replaced by `@appsetting()` references. */
  authentication: AgentAuthentication | undefined;
  /** Map of app-setting name -> raw secret value to upsert into environment settings. */
  appSettings: Record<string, string>;
}

/**
 * Sensitive leaf names on an agent-connection authentication object. Only
 * these fields are eligible to be moved to app settings. Other auth metadata
 * (`type`, `identity`, `scheme`) stays inline in connections.json.
 */
const SECRET_FIELDS = ['key', 'parameter', 'value'] as const;

/**
 * Move secret leaves on `authentication` to app settings and replace each
 * leaf with `@appsetting('NAME')`. Already-referenced leaves are left in
 * place (idempotent re-save). Empty / missing leaves are skipped.
 */
export function extractAgentConnectionAppSettings(
  connectionKey: string,
  authentication: AgentAuthentication | undefined,
): AgentAuthenticationAppSettingsResult {
  if (!authentication) {
    return { authentication, appSettings: {} };
  }

  const next: AgentAuthentication = { ...authentication };
  const appSettings: Record<string, string> = {};

  for (const field of SECRET_FIELDS) {
    const value = next[field];
    if (typeof value !== 'string' || value === '') continue;
    if (isAppSettingReference(value)) continue;

    const settingName = formatAppSettingName(connectionKey, field);
    appSettings[settingName] = value;
    next[field] = formatAppSettingReference(settingName);
  }

  return { authentication: next, appSettings };
}
